//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useCallback, useEffect, useState, useContext } from 'react';
import { makeStyles, Grid } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Drawer from '@material-ui/core/Drawer';
import CssBaseline from '@material-ui/core/CssBaseline';
import AppBar from '@material-ui/core/AppBar';
import List from '@material-ui/core/List';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';

// import brandLogo from '../../images/brandLogo.png';
// import instagram from '../../images/drawer/instagram.png';
// import facebook from '../../images/drawer/facebook.png';
// import pinterest from '../../images/drawer/pinterest.png';
// import twitter from '../../images/drawer/twitter.png';

import LinkedInIcon from '@material-ui/icons/LinkedIn';
import YouTubeIcon from '@material-ui/icons/YouTube';
import FacebookIcon from '@material-ui/icons/Facebook';
import InstagramIcon from '@material-ui/icons/Instagram';
import TiktokIcon from '../../assets/tiktok_logo_4509.svg';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import {
    PATH_DASHBOARD,
    PATH_HOME,
    PATH_RESOURCE_CENTER,
    PATH_SUB_PAGE_TERMS,
    PATH_SUB_PAGE_PRIVACY,
    PATH_SUB_PAGE_MESSAGE,
    QUERY_PARAM_NAME_MODE,
    TOP_NAV_MODE_LOGIN,
    TOP_NAV_MODE_SIGN_UP,
} from '../../system/constants';
import { AppContext, LayoutContext } from '../../common/contexts';
import {
    BASE_URL,
    FACEBOOK_SOCIAL_URL,
    HOMEPAGE_ABOUT,
    HOMEPAGE_CONTACT,
    HOMEPAGE_EMPLOYERS,
    HOMEPAGE_FAQ,
    INSTAGRAM_SOCIAL_URL,
    LINKEDIN_SOCIAL_URL,
    TIKTOK_SOCIAL_URL,
    USER_TYPE_EMP,
    USER_TYPE_JS,
    YOUTUBE_SOCIAL_URL,
} from '../../common/constants';
import { setClassNameInDefns } from '../../common/definitionHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { isOnPage } from '../../system/helpers/systemHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdBox from '../../common/elemental/AfitdBox';
import AfitdButton from '../../common/elemental/AfitdButton';
import Logo from '../../system/Logo';
import LogoSm from '../../system/LogoSm';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import TopNavButton from './TopNavButton';
import LoginDialog from './LoginDialog';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _noop from 'lodash/noop';
import { useQuery } from '../../common/hooks/useQuery';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const drawerWidth = '100%';
const useStyles = (params = {}) => {
    // const { lgMatches } = params;

    return makeStyles(theme => ({
        topNavSectionContainer: {
            background: 'white',
            width: '100%',
            height: '80px',
            borderBottom: '1px solid #F0F2F6',
        },
        root: {
            display: 'flex',
        },
        appLogoContainer: { cursor: 'pointer', marginTop: 10, [theme.breakpoints.down('md')]: { marginTop: 6 } },
        // appLogo: {
        //     width: lgMatches ? 32 : 165,
        // },
        buttonContainer: {
            marginLeft: '40px',
            [theme.breakpoints.down('lg')]: { marginLeft: '20px' },
            [theme.breakpoints.down('sm')]: { marginLeft: '40px' },
        },
        loginButtonContainer: {
            height: '100%',
            marginLeft: '40px',
            [theme.breakpoints.down('lg')]: { marginLeft: '-28px' },
            [theme.breakpoints.down('sm')]: { marginRight: '-35px', marginTop: 30 }, //[theme.breakpoints.down('md')]: { marginRight: '-35px', marginTop: 30 },
        },
        avatarBorder: {
            width: theme.spacing(5),
            height: theme.spacing(5),
            borderRadius: '50%',
            padding: '3px',
            background: '-webkit-linear-gradient(360deg, #f6b73c, #1D8A8B)',
            // As fallback for browsers which do not support gradient
            fallbacks: [
                { background: '#f6b73c' },
                { background: '-o-linear-gradient(360deg, #f6b73c, #1D8A8B)' },
                { background: '-moz-linear-gradient(360deg, #f6b73c, #1D8A8B)' },
                { background: 'linear-gradient(360deg, #f6b73c, #1D8A8B)' },
            ],
        },
        avatar: {
            width: theme.spacing(5),
            height: theme.spacing(5),
            borderRadius: '100%',
            cursor: 'pointer',
        },

        // copied form main topNav
        leftContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            [theme.breakpoints.down('lg')]: { justifyContent: 'flex-start' },
        },
        middleContainer: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
        },
        middleContainer720: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            background: 'white',
            borderBottom: '1px solid #F0F2F6',
            width: '100%',
        },

        rightContainer: {
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
        },
        resHeight: {
            height: 62,
        },
        menuContainer: {
            marginRight: '30px',
            [theme.breakpoints.down('lg')]: { marginLeft: '20px' },
            [theme.breakpoints.down('sm')]: { marginLeft: '40px' }, //[theme.breakpoints.down('md')]: { marginLeft: '40px' },
        },
        drawerListContainer: {
            paddingLeft: 24,
        },
        drawerList1: { fontSize: 20, fontWeight: 'bold', cursor: 'pointer' },
        drawerList2Container: {
            borderTop: '1px solid #F0F2F6',
            marginLeft: '24px',
            marginRight: 24,
            marginBottom: 40,
        },
        drawerList2: { cursor: 'pointer', fontSize: 16 },
        // getStartedButton: {
        //     backgroundColor: '#FC821F',
        //     borderRadius: '4px',
        //     padding: '13px 16px',
        //     margin: ' 0 auto',
        //     border: 'none',
        //     color: 'white',
        //     fontSize: '20px',
        //     '&:hover': { cursor: 'pointer' },
        //     boxSizing: 'border-box',
        //     marginTop: 14,
        // },
        getStartedButtonDrawer: {
            backgroundColor: '#FC821F',
            borderRadius: '4px',
            padding: '10px 33px',
            margin: ' 0 auto',
            border: 'none',
            color: 'white',
            fontSize: '20px',
            '&:hover': { cursor: 'pointer' },
            boxSizing: 'border-box',
        },
        loginButtonDrawer: {
            background: 'rgba(0, 0, 0, 0.04)',
            borderRadius: '4px',
            padding: '10px 52px',
            margin: ' 0 auto',
            border: 'none',
            fontSize: '20px',
            '&:hover': { cursor: 'pointer' },
            boxSizing: 'border-box',
            [theme.breakpoints.down('xs')]: { marginBottom: '16px' },
        },
        // appLogo: {
        //     width: '45%',
        // },
        topNavContainer: { justifyContent: 'center', alignItems: 'center' },
        topNavContent2Ul: {
            listStyleType: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        topNavContent2UlLi: {
            fontSize: 16,
        },
        appBar: {
            top: 0,
            left: 0,
            width: 1,
            background: '#FAFAFC',
            boxShadow: 'none',
            transition: theme.transitions.create(['margin', 'width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
        },
        appBarShift: {
            width: `calc(100% - ${drawerWidth}px)`,
            marginLeft: drawerWidth,
            transition: theme.transitions.create(['margin', 'width'], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        menuButton: {
            marginRight: theme.spacing(2),
            color: '#111E14',
        },
        hide: {
            display: 'none',
        },
        drawer: {
            width: drawerWidth,
            flexShrink: 0,
        },
        drawerPaper: {
            width: drawerWidth,
            marginTop: 78,
            background: 'white',
            borderTop: '1px solid #F0F2F6',
        },
        drawerHeader: {
            display: 'flex',
            alignItems: 'center',
            padding: theme.spacing(0, 1),
            // necessary for content to be below app bar
            ...theme.mixins.toolbar,
            justifyContent: 'flex-end',
        },
        content: {
            flexGrow: 1,
            padding: theme.spacing(3),
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: -drawerWidth,
        },
        contentShift: {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: 0,
        },
        homeLogo: {
            width: 32,
            height: 32,
        },
        drawerButtonContent1: {
            marginRight: '20px',
            marginLeft: 24,
            [theme.breakpoints.down('xs')]: { marginRight: '0px' },
        },
        drawerIconsContainer: { marginTop: 30, paddingLeft: 24 },
        drawerIconContent: {
            marginRight: 20,
            cursor: 'pointer',
        },
        tiktokIcon: {
            cursor: 'pointer',
            // filter: 'invert(50%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)',
            margin: '0px 0px',
            marginTop: 2,
        },
        iconContent: {
            fontSize: '32px',
            cursor: 'pointer',
            color: '#000',
            margin: '0px 0px',
        },
        youtubeIconContent: {
            fontSize: '34px',
            cursor: 'pointer',
            color: '#000',
            margin: '0px 0px',
        },
        menuBarIcon: {
            marginRight: 19,
            color: 'rgba(17, 30, 20, 1)',
            cursor: 'pointer',
        },
        closeBarIcon: {
            marginRight: 19,
            color: 'rgba(17, 30, 20, 1)',
            cursor: 'pointer',
        },
        topNavSectionContent: {},
        homeButton: {
            width: 32,
            marginRight: '10px',
            [theme.breakpoints.down('sm')]: { marginRight: '0px' }, //[theme.breakpoints.down('md')]: { marginRight: '0px' },
        },
        drawerButtonContainer: {},
        buttonContainerModal: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '50px 0', marginBottom: '100px' },
        modalStyle1: {
            position: 'absolute',
            top: '10%',
            left: '10%',
            overflow: 'scroll',
            height: '100%',
            display: 'block',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        // props,
        classes,
        history,
        location,
        handleClickLogo,
        loginDialogOpen,
        handleOpenLoginModal,
        handleCloseLoginDialog,
        isLoginMode,
        // setIsLoginMode,
        hideCreateAccountJsOption,
        // setHideCreateAccountJsOption,
        hideCreateAccountEmpOption,
        // setHideCreateAccountEmpOption,
    } = params;
    const { buttonContainer: buttonContainerCls } = classes;

    const dashboardButtonIsHidden = isOnPage(location, PATH_DASHBOARD);

    const defns = {
        appLogoContainer: { onClick: handleClickLogo },
        dashboardButtonContainer: {
            isHidden: dashboardButtonIsHidden,
            className: buttonContainerCls,
        },
        resourceCenterButton: {
            onClick: handleClickButton.bind(null, history, PATH_RESOURCE_CENTER),
            label: 'Resource Center',
        },
        employersButton: {
            onClick: handleClickButton.bind(null, history, `${PATH_HOME}/${HOMEPAGE_EMPLOYERS}`),
            label: 'Employers',
        },
        aboutButton: {
            onClick: handleClickButton.bind(null, history, `${PATH_HOME}/${HOMEPAGE_ABOUT}`),
            label: 'About Us',
        },
        resourceCenterLink: {
            onClick: handleClickDrawerLink.bind(null, history, PATH_RESOURCE_CENTER),
        },
        employersLink: {
            onClick: handleClickDrawerLink.bind(null, history, `${PATH_HOME}/${HOMEPAGE_EMPLOYERS}`),
        },
        aboutLink: {
            onClick: handleClickDrawerLink.bind(null, history, `${PATH_HOME}/${HOMEPAGE_ABOUT}`),
        },
        contactUsLink: {
            onClick: handleClickDrawerLink.bind(null, history, `${PATH_HOME}/${HOMEPAGE_CONTACT}`),
        },
        theMessageLink: {
            onClick: handleClickDrawerLink.bind(null, history, `${PATH_HOME}/${HOMEPAGE_FAQ}/${PATH_SUB_PAGE_MESSAGE}`),
        },
        termsOfUseLink: {
            onClick: handleClickDrawerLink.bind(null, history, `${PATH_HOME}/${HOMEPAGE_FAQ}/${PATH_SUB_PAGE_TERMS}`),
        },
        privacyLink: {
            onClick: handleClickDrawerLink.bind(null, history, `${PATH_HOME}/${HOMEPAGE_FAQ}/${PATH_SUB_PAGE_PRIVACY}`),
        },
        loginButton: {
            onClick: handleOpenLoginModal.bind(null, true),
            label: 'Log In',
        },
        loginButtonDrawer: {
            onClick: handleOpenLoginModal.bind(null, true),
            label: 'Log In',
        },
        getStartedButton: {
            color: 'primary',
            onClick: handleOpenLoginModal.bind(null, false),
            label: 'Get Started',
        },
        getStartedButtonDrawer: {
            color: 'primary',
            onClick: handleOpenLoginModal.bind(null, false),
            label: 'Get Started',
        },
        homeButton: { onClick: handleClickButton.bind(null, history, '') },
        topNavSectionContainer: { container: true, direction: 'row', justify: 'center' },
        topNavSectionContent: { container: true, direction: 'row', item: true, xl: 10, lg: 11, md: 11, sm: 11, xs: 11 },

        leftContainer: { item: true, xl: 3, lg: 3, md: 1, sm: 10, xs: 10 },
        middleContainer: { item: true, xl: 6, lg: 6, md: 8, sm: 12, xs: 12 },
        rightContainer: { item: true, xl: 3, lg: 3, md: 3, sm: 2, xs: 2 },
        middleContainer720: { item: true, xl: 6, lg: 6, md: 12, sm: 12, xs: 12 },
        drawerButtonContainer: { container: true, direction: 'row', justify: 'flex-start' },
        drawerButtonContent1: { item: true },
        drawerButtonContent2: { item: true },
        drawerIconsContainer: { container: true, direction: 'row' },
        drawerIconContent: { item: true },
        loginDialog: {
            open: loginDialogOpen,
            isLoginMode,
            hideJS: hideCreateAccountJsOption,
            hideEmp: hideCreateAccountEmpOption,
            onClose: handleCloseLoginDialog,
        },
        tiktokIcon: { src: TiktokIcon, width: '28px' },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handleClickButton = (history, path) => {
    history.push(`${BASE_URL}${path}`);
};

const handleClickDrawerLink = (history, path) => {
    history.push(`${BASE_URL}${path}`, { fromDrawer: true });
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const TopNavPublic = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { auth, isAuthenticationPending } = appContext;

    const layoutContext = useContext(LayoutContext);
    const {
        setLoadingDialogOpen,
        isRequestOpenLoginDialog,
        setIsRequestOpenLoginDialog = _noop,
        isRequestOpenCreateAccountDialog,
        setIsRequestOpenCreateAccountDialog = _noop,
    } = layoutContext;

    const query = useQuery();
    const mode = query.get(QUERY_PARAM_NAME_MODE);

    const theme = useTheme();
    const xlMatches = useMediaQuery(theme.breakpoints.down('xl'));
    const lgMatches = useMediaQuery(theme.breakpoints.down('lg'));
    const mdMatches = useMediaQuery(theme.breakpoints.down('md'));
    const mdMatchesUp = useMediaQuery(theme.breakpoints.up('md'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    const [open, setOpen] = React.useState(false);
    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const history = useHistory();
    const location = useLocation();
    const isPromptLogin = _get(location, 'state.isPromptLogin', false);
    const isPromptCreateAccount = _get(location, 'state.isPromptCreateAccount', false);
    const userType = _get(location, 'state.userType');

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // login or register modal
    const [loginDialogOpen, setLoginDialogOpen] = useState(false);

    const [isLoginMode, setIsLoginMode] = useState(false);
    const [hideCreateAccountJsOption, setHideCreateAccountJsOption] = useState(false);
    const [hideCreateAccountEmpOption, setHideCreateAccountEmpOption] = useState(false);

    const handleOpenLoginModal = isLoginMode => {
        setIsLoginMode(isLoginMode);
        setLoginDialogOpen(true);
        if (isLoginMode) {
            // since user explicitly open the Login dialog, always redirect to Dashboard default page (My Calendar) AFTER login
            history.replace({ ...location, state: { isPromptLogin: true, redirectPathname: `${BASE_URL}${PATH_DASHBOARD}` } });
        }
    };

    const handleCloseLoginDialog = () => {
        setLoginDialogOpen(false);
        setHideCreateAccountEmpOption(false);
        setHideCreateAccountJsOption(false);
    };
    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // show authenticating progress dialog or login dialog
    useEffect(() => {
        // if auth not initialized yet or still authenticating, show progress dialog
        if (!auth || isAuthenticationPending) {
            setLoadingDialogOpen(true);
        } else if (auth) {
            // otherwise, if auth is initialized, hide progress dialog
            setLoadingDialogOpen(false);
            // and show login dialog if requested (from router location states)
            if (isPromptLogin || mode === TOP_NAV_MODE_LOGIN) {
                setLoginDialogOpen(true);
                setIsLoginMode(true);
            } else if (isPromptCreateAccount || mode === TOP_NAV_MODE_SIGN_UP) {
                setLoginDialogOpen(true);
                setIsLoginMode(false);
                setHideCreateAccountEmpOption(userType === USER_TYPE_JS);
                setHideCreateAccountJsOption(userType === USER_TYPE_EMP);
            }
        }
    }, [auth, isAuthenticationPending, isPromptLogin, isPromptCreateAccount, userType, setLoadingDialogOpen, mode]);

    // useEffect(() => {
    //     // and show login dialog if requested (from router location states)
    //     if (isPromptLogin) {
    //         setLoginDialogOpen(true);
    //         setIsLoginMode(true);
    //     }
    // }, [isPromptLogin]);

    useEffect(() => {
        if (isRequestOpenLoginDialog) {
            setIsLoginMode(true);
            setLoginDialogOpen(true);
            setIsRequestOpenLoginDialog(false);
        }
    }, [isRequestOpenLoginDialog, setIsRequestOpenLoginDialog]);

    useEffect(() => {
        if (isRequestOpenCreateAccountDialog) {
            setIsLoginMode(false);
            setLoginDialogOpen(true);
            setIsRequestOpenCreateAccountDialog(false);
        }
    }, [isRequestOpenCreateAccountDialog, setIsRequestOpenCreateAccountDialog]);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickLogo = useCallback(() => {
        history.push(`${BASE_URL}${PATH_HOME}`);
    }, [history]);

    const handleClickIcons = url => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const classes = useStyles({ lgMatches })();
    if (props.isHidden) return null;
    const defns = getDefns({
        props,
        classes,
        history,
        location,
        handleClickLogo,
        loginDialogOpen,
        handleOpenLoginModal,
        handleCloseLoginDialog,
        isLoginMode,
        setIsLoginMode,
        hideCreateAccountJsOption,
        setHideCreateAccountJsOption,
        hideCreateAccountEmpOption,
        setHideCreateAccountEmpOption,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div>
            <Grid {...defns.topNavSectionContainer}>
                <Grid {...defns.topNavSectionContent}>
                    {/* ================== Left Logo/Menu ================== */}
                    <Grid {...defns.leftContainer}>
                        {mdMatches ? (
                            mdMatchesUp ? (
                                /* ------------------ 768 - 1023 ------------------ */
                                <div {...defns.appLogoContainer}>
                                    <LogoSm {...defns.appLogo} {...defns.homeButton} />
                                </div>
                            ) : open ? (
                                /* ------------------ 0 - 767 (drawer opened) ------------------ */
                                <CloseIcon onClick={handleDrawerClose} {...defns.closeBarIcon} />
                            ) : (
                                /* ------------------ 0 - 767 (drawer closed) ------------------ */
                                <MenuIcon onClick={handleDrawerOpen} {...defns.menuBarIcon} />
                            )
                        ) : lgMatches || xlMatches ? (
                            /* ------------------ 1024 or up ------------------ */
                            <div {...defns.appLogoContainer}>
                                <Logo {...defns.appLogo} />
                            </div>
                        ) : null}
                    </Grid>
                    {/* ================== Middle Nav Links ================== */}
                    {xlMatches && mdMatchesUp && (
                        /* ------------------ 768 or up ------------------ */
                        <Grid {...defns.middleContainer}>
                            <CssBaseline />
                            <AfitdBox {...defns.buttonContainer}>
                                <TopNavButton {...defns.resourceCenterButton} />
                            </AfitdBox>
                            <AfitdBox {...defns.buttonContainer}>
                                <TopNavButton {...defns.employersButton} />
                            </AfitdBox>
                            <AfitdBox {...defns.buttonContainer}>
                                <TopNavButton {...defns.aboutButton} />
                            </AfitdBox>
                        </Grid>
                    )}
                    {/* ================== Right Logo/Login ================== */}
                    {smMatches ? (
                        /* ------------------ 0 - 359 ------------------ */
                        <Grid {...defns.rightContainer}>
                            <div {...defns.appLogoContainer}>
                                <LogoSm {...defns.appLogo} {...defns.homeButton} />
                            </div>
                        </Grid>
                    ) : (
                        /* ------------------ 360 or up ------------------ */
                        <AfitdBox {...defns.rightContainer}>
                            <AfitdBox {...defns.loginButtonContainer}>
                                <TopNavButton {...defns.loginButton} />
                            </AfitdBox>
                            <AfitdBox {...defns.buttonContainer}>
                                <AfitdButton {...defns.getStartedButton} />
                            </AfitdBox>
                        </AfitdBox>
                    )}
                </Grid>
            </Grid>
            {/* ================== Middle Nav Links (2nd Row) (No TEMP menu) ================== */}
            {mdMatches && mdMatchesUp && false && (
                /* ------------------ 768 - 1023 (disabled) ------------------ */
                <div {...defns.middleContainer720}>
                    <TopNavButton {...defns.resourceCenterButton} />
                    <TopNavButton {...defns.employersButton} />
                    <TopNavButton {...defns.aboutButton} />
                </div>
            )}

            {/* ================== Drawer Section ================== */}
            {smMatches && (
                /* ------------------ below 768 ------------------ */
                <div className={classes.root}>
                    <CssBaseline />
                    <AppBar position="fixed"></AppBar>
                    <Drawer
                        className={classes.drawer}
                        variant="persistent"
                        anchor="left"
                        open={open}
                        classes={{
                            paper: classes.drawerPaper,
                        }}
                    >
                        <List {...defns.drawerListContainer}>
                            <p {...defns.resourceCenterLink} {...defns.drawerList1}>
                                <span onClick={handleDrawerClose}>Resource Center</span>
                            </p>
                            <p {...defns.employersLink} {...defns.drawerList1}>
                                <span onClick={handleDrawerClose}>Employers</span>
                            </p>
                            <p {...defns.aboutLink} {...defns.drawerList1}>
                                <span onClick={handleDrawerClose}>About Us</span>
                            </p>
                        </List>
                        <List {...defns.drawerList2Container}>
                            <p {...defns.theMessageLink} {...defns.drawerList2}>
                                <span onClick={handleDrawerClose}>The Message</span>
                            </p>
                            <p {...defns.termsOfUseLink} {...defns.drawerList2}>
                                <span onClick={handleDrawerClose}>Terms of Use</span>
                            </p>
                            <p {...defns.privacyLink} {...defns.drawerList2}>
                                <span onClick={handleDrawerClose}>Privacy Policy</span>
                            </p>
                            <p {...defns.contactUsLink} {...defns.drawerList2}>
                                <span onClick={handleDrawerClose}>Contact Us</span>
                            </p>
                        </List>
                        <Grid {...defns.drawerButtonContainer}>
                            <Grid {...defns.drawerButtonContent1} onClick={handleDrawerClose}>
                                <AfitdButton {...defns.loginButtonDrawer} />
                            </Grid>
                            <Grid {...defns.drawerButtonContent2} onClick={handleDrawerClose}>
                                <AfitdButton {...defns.getStartedButtonDrawer} />
                            </Grid>
                        </Grid>

                        <Grid {...defns.drawerIconsContainer}>
                            <Grid {...defns.drawerIconContent}>
                                <img alt="TikTok Icon" {...defns.tiktokIcon} onClick={() => handleClickIcons(TIKTOK_SOCIAL_URL)} />
                            </Grid>
                            <Grid {...defns.drawerIconContent}>
                                <InstagramIcon {...defns.iconContent} onClick={() => handleClickIcons(INSTAGRAM_SOCIAL_URL)} />
                                {/* <img src={instagram} alt="" /> */}
                            </Grid>
                            <Grid {...defns.drawerIconContent}>
                                <FacebookIcon {...defns.iconContent} onClick={() => handleClickIcons(FACEBOOK_SOCIAL_URL)} />
                                {/* <img src={facebook} alt="" /> */}
                            </Grid>
                            <Grid {...defns.drawerIconContent}>
                                <LinkedInIcon {...defns.iconContent} onClick={() => handleClickIcons(LINKEDIN_SOCIAL_URL)} />
                                {/* <img src={pinterest} alt="" /> */}
                            </Grid>
                            <Grid {...defns.drawerIconContent}>
                                <YouTubeIcon {...defns.youtubeIconContent} onClick={() => handleClickIcons(YOUTUBE_SOCIAL_URL)} />
                                {/* <img src={twitter} alt="" /> */}
                            </Grid>
                        </Grid>
                    </Drawer>
                </div>
            )}

            {/* ================== Login/Register Modal Section ================== */}
            <LoginDialog {...defns.loginDialog} />
        </div>
        //     </div>
        // </div>
    );
};

export default TopNavPublic;
